import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react'
import { tenantStorageKey } from './tenantStorage'
import { isHubApiPath } from './apiRoutingPolicy'
import { readOfflineServerInventory, writeOfflineServerInventory } from './offlineCache'
import { getAuthToken } from './tokenStorage'
import { loadServerInventory } from './serverInventoryRequest'
import { serverSelectionStorage } from './serverSelectionStorage'

import type { InstalledSystemInfo } from './serverSystemInfo'

export interface ServerOption {
  id: string
  name: string
  host: string
  is_hub: boolean
  reachable: boolean
  status: 'ok' | 'offline' | 'installing' | 'provisioning' | 'failed'
  transport?: 'wireguard' | 'direct' | string
  wg_ip?: string
  wireguard_available?: boolean
  wireguard_unavailable_reason?: string
  os?: string
  os_info?: InstalledSystemInfo | null
  arch?: string
  features?: string[]
  executables?: Record<string, boolean>
  unavailable_features?: Record<string, string>
  stats?: {
    cpu_percent?: number
    mem_percent?: number
    mem_used_gb?: number
    mem_total_gb?: number
  } | null
}

interface ServerContextValue {
  servers: ServerOption[]
  activeServer: string | null // İşletim sistemi hedefi; hub meta API'leri ayrıca korunur.
  setActiveServer: (name: string | null) => void
  refresh: () => Promise<void>
  connectionState: 'loading' | 'live' | 'offline' | 'unreachable'
  inventorySource: 'live' | 'cache' | 'none'
  inventoryUpdatedAt: number | null
}

const ServerContext = createContext<ServerContextValue | null>(null)
const STORAGE_KEY = 'scpActiveServer'
const PENDING_STORAGE_KEY = 'scpPendingActiveServer'
// NOT: "ana-agent" ÖNCEDEN kod içinde SABİT bir isim olarak taşınıyordu — hub
// hem UI'da özel bir dropdown seçeneği olarak ayrı gösteriliyor hem de bu
// isimle karşılaştırılıyordu, yani kayıt yeniden adlandırılamaz/silinemezdi.
// Artık hub, backend'in /api/servers yanıtındaki `is_hub` alanıyla tanınıyor;
// HUB_NAME_CACHE_KEY sadece React dışı senkron okuma (axios interceptor gibi)
// için EN SON bilinen hub adını önbelleğe alır — sabit kodlanmış bir isim
// DEĞİL, sadece ilk yüklemeden önceki geçici bir başlangıç değeri.
const HUB_NAME_CACHE_KEY = 'scpHubServerName'
const NO_SERVER_SENTINEL = '__servernox_no_server__'
export const AUTH_CHANGED_EVENT = 'servernox:auth-changed'

function scopedKey(base: string): string {
  return tenantStorageKey(base)
}

function storeActiveServer(name: string): void {
  const key = scopedKey(STORAGE_KEY)
  serverSelectionStorage.set(key, name)
}

function getPendingActiveServer(): string {
  try {
    return sessionStorage.getItem(scopedKey(PENDING_STORAGE_KEY)) || ''
  } catch {
    return ''
  }
}

function storePendingActiveServer(name: string): void {
  try {
    const key = scopedKey(PENDING_STORAGE_KEY)
    if (name) sessionStorage.setItem(key, name)
    else sessionStorage.removeItem(key)
  } catch {
    // localStorage remains the durable source when sessionStorage is blocked.
  }
}

function getHubServerName(): string {
  return serverSelectionStorage.get(scopedKey(HUB_NAME_CACHE_KEY))
}

// Bu iki alanların (özellikle hangi sunucunun aktif olduğu) React dışından
// (axios interceptor, SSE job runner gibi hook kullanamayan yerlerden) da
// okunması gerekiyor — bu yüzden context'e ek olarak düz fonksiyonlar da
// export ediliyor.
export function getActiveServer(): string {
  return serverSelectionStorage.get(scopedKey(STORAGE_KEY)) || getHubServerName()
}

// Sunucu ekleme/silme/bildirim ayarı gibi HER ZAMAN hub'da çalışması gereken
// "meta" uçlar — bir uzak sunucu seçiliyken bile bunlar proxy'lenmemeli.
/** '/api/...' ile başlayan bir yolu, aktif sunucuya göre proxy yoluna çevirir. */
export function resolveApiPath(path: string): string {
  const activeServer = getActiveServer() || NO_SERVER_SENTINEL
  if (!path.startsWith('/api/')) return path
  // An explicit hub proxy path already carries its target server. Re-proxying
  // it would turn /api/proxy/{target}/... into
  // /api/proxy/{active}/proxy/{target}/..., which breaks downloads initiated
  // from an AI conversation that belongs to another (or the same) server.
  if (path.startsWith('/api/proxy/')) return path
  const rest = path.slice(4) // baştaki '/api' kısmını at
  if (isHubApiPath(rest)) return path
  return `/api/proxy/${activeServer}${rest}`
}

/** axios config.url gibi baseURL='/api' varsayan göreli yolları çevirir (başında '/api' YOK). */
export function resolveRelativeApiPath(url: string): string {
  // Explicit hub proxy paths are already server-scoped. Re-proxying these
  // would produce /proxy/{active}/proxy/{target}/... and break terminals that
  // deliberately target a server other than the currently selected one.
  if (url.startsWith('/proxy/')) return url
  if (isHubApiPath(url)) return url
  const activeServer = getActiveServer() || NO_SERVER_SENTINEL
  return `/proxy/${activeServer}${url}`
}

export function ServerProvider({ children }: { children: ReactNode }) {
  const [initialInventory] = useState(() => readOfflineServerInventory())
  const [servers, setServers] = useState<ServerOption[]>(() => initialInventory?.servers || [])
  const [activeServer, setActiveServerState] = useState<string | null>(getPendingActiveServer() || getActiveServer())
  const [connectionState, setConnectionState] = useState<'loading' | 'live' | 'offline' | 'unreachable'>(
    () => navigator.onLine === false ? 'offline' : 'loading',
  )
  const [inventorySource, setInventorySource] = useState<'live' | 'cache' | 'none'>(
    () => initialInventory ? 'cache' : 'none',
  )
  const [inventoryUpdatedAt, setInventoryUpdatedAt] = useState<number | null>(initialInventory?.updatedAt || null)

  const refreshGeneration = useRef(0)
  const lastRefreshAt = useRef(0)
  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current
    const token = getAuthToken()
    const isCurrent = () => generation === refreshGeneration.current && token === getAuthToken()
    if (navigator.onLine === false) {
      setConnectionState('offline')
      setInventorySource(current => current === 'live' ? 'cache' : current)
      setServers(current => current.map(server => ({ ...server, reachable: false })))
      return
    }
    try {
      if (!token) return
      const data = await loadServerInventory<ServerOption>(token)
      if (!isCurrent()) return
      if (!Array.isArray(data)) throw new Error('Invalid server inventory response')
      // A real offline agent remains selectable/visible for diagnostics. Only
      // unfinished and failed registrations are managed on the Servers page;
      // hiding an offline row made a peer outage look like data loss.
      const list: ServerOption[] = Array.isArray(data)
        ? data.filter((s: ServerOption) => !['installing', 'provisioning', 'failed'].includes(s.status))
        : []
      setServers(list)
      const cached = writeOfflineServerInventory(list, localStorage, getPendingActiveServer() || getActiveServer())
      setInventoryUpdatedAt(cached.updatedAt)
      setInventorySource('live')
      setConnectionState('live')
      // getActiveServer() React dışından senkron çağrılabildiği için hub'ın
      // adını her fetch'te önbelleğe alıyoruz — bkz. HUB_NAME_CACHE_KEY notu.
      const hub = list.find(s => s.is_hub)
      const hubKey = scopedKey(HUB_NAME_CACHE_KEY)
      serverSelectionStorage.set(hubKey, hub?.name || '')

      // Seçim yalnızca mevcut üyeliğin erişebildiği listeden gelebilir. Hesap
      // veya organizasyon değişiminde önceki tenant'ın sunucusunu taşımak hem
      // hatalı proxy isteklerine hem de yerel hub metriklerine düşmeye yol
      // açıyordu. Hub yoksa ilk erişilebilir uzak sunucu güvenli varsayılandır.
      // A full reload is intentional because a number of legacy pages fetch
      // only on mount. Keep the just-clicked selection in sessionStorage as a
      // one-reload handoff so an in-flight refresh or WebView lifecycle event
      // cannot restore the previous server during navigation.
      const pending = getPendingActiveServer()
      const remembered = pending || getActiveServer()
      const selected = list.find(s => s.name === remembered) || hub || list[0]
      storeActiveServer(selected?.name || '')
      setActiveServerState(selected?.name || null)
      if (pending) storePendingActiveServer('')
    } catch {
      if (!isCurrent()) return
      // Ağ/backend hatası gerçek "sunucu yok" değildir. Son doğrulanmış
      // envanteri salt okunur tut; UI bunun canlı olmadığını açıkça etiketler.
      const cached = readOfflineServerInventory()
      if (cached) {
        setServers(current => current.length ? current.map(server => ({ ...server, reachable: false })) : cached.servers)
        setInventoryUpdatedAt(cached.updatedAt)
        setInventorySource('cache')
      } else {
        setInventorySource('none')
      }
      const currentlyOffline = Reflect.get(window.navigator, 'onLine') === false
      setConnectionState(currentlyOffline ? 'offline' : 'unreachable')
    } finally {
      if (isCurrent()) lastRefreshAt.current = Date.now()
    }
  }, [])

  useEffect(() => {
    void refresh()
    const onAuthChanged = () => {
      const cached = readOfflineServerInventory()
      setServers(cached?.servers || [])
      setInventoryUpdatedAt(cached?.updatedAt || null)
      setInventorySource(cached ? 'cache' : 'none')
      setConnectionState(navigator.onLine === false ? 'offline' : 'loading')
      setActiveServerState(getActiveServer() || null)
      void refresh()
    }
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
    const refreshVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefreshAt.current >= 5_000) void refresh()
    }
    window.addEventListener('focus', refreshVisible)
    const onConnectivityChanged = () => { lastRefreshAt.current = 0; void refresh() }
    window.addEventListener('online', onConnectivityChanged)
    window.addEventListener('offline', onConnectivityChanged)
    document.addEventListener('visibilitychange', refreshVisible)
    const timer = window.setInterval(refreshVisible, 30_000)
    return () => {
      refreshGeneration.current++
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
      window.removeEventListener('focus', refreshVisible)
      window.removeEventListener('online', onConnectivityChanged)
      window.removeEventListener('offline', onConnectivityChanged)
      document.removeEventListener('visibilitychange', refreshVisible)
      window.clearInterval(timer)
    }
  }, [refresh])

  function setActiveServer(name: string | null) {
    const target = name || getHubServerName()
    if (target && !servers.some(server => server.name === target)) return
    storePendingActiveServer(target)
    storeActiveServer(target)
    setActiveServerState(target)
    // Basit ve güvenilir yaklaşım: sunucu değişince sayfayı yenile — her
    // sayfanın kendi state'i (nginx site listesi, docker konteynerleri vb.)
    // baştan, DOĞRU sunucudan çekilmiş olsun; aksi halde onlarca sayfada
    // "aktif sunucu değişti" efekti eklemek gerekirdi.
    window.location.reload()
  }

  return (
    <ServerContext.Provider value={{ servers, activeServer, setActiveServer, refresh, connectionState, inventorySource, inventoryUpdatedAt }}>
      {children}
    </ServerContext.Provider>
  )
}

export function useServer(): ServerContextValue {
  const ctx = useContext(ServerContext)
  if (!ctx) throw new Error('useServer, ServerProvider içinde kullanılmalı')
  return ctx
}
